import WorkContextBar from './components/WorkContextBar';
import React, { Suspense, useLayoutEffect, useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';

import TrialCountdownBanner from './components/TrialCountdownBanner';
import PaywallModal from './components/PaywallModal';
import InAppMessageDelivery from './components/inapp-messages/InAppMessageDelivery';
import billingService from './services/billingService';

import websocketService from './services/websocketService';
import {
  ACCESS_TOKEN_REFRESHED_EVENT,
  clearAuth,
  ensureFreshAccessToken,
  getApiBaseUrl,
  getRoleFromToken,
  isAccessTokenNearExpiry,
  scheduleAccessTokenRefresh,
  verifyOrRefreshSession,
} from './services/api';
import { isDemoModeActive } from './demo/demoMode';


// User Dashboard


import { UserTrackSettingsProvider } from './user-dashboard/context/UserTrackSettingsContext';
import { resetAppSession } from './utils/resetAppSession';
import { startSessionPresence, stopSessionPresence } from './utils/sessionPresence';
import UnifiedState from './components/UnifiedState';
import RouteFallback from './components/RouteFallback';
import UserPermissionNotice from './user-dashboard/components/UserPermissionNotice';
import RouteLoadingState from './components/RouteLoadingState';
import lazyWithRetry from './utils/lazyWithRetry';
import { appHref, clearSessionCookie, getAppOrigin, getMarketingOrigin, getSiteMode, setSessionCookie } from './utils/siteHosts';
import authService from './services/authService';
import { setServerMaintenanceActive } from './services/serverMaintenance';






import { appIcon } from './assets/catalog';
import FeedbackWidget from './components/FeedbackWidget';
import GlobalVoiceInput from './components/GlobalVoiceInput';
import { trackPageView } from './services/userTrackingService';

const Sidebar = lazyWithRetry(() => import('./components/Sidebar'), 'sidebar');
const Header = lazyWithRetry(() => import('./components/Header'), 'header');
const UserSidebar = lazyWithRetry(() => import('./user-dashboard/components/UserSidebar'), 'usersidebar');
const UserHeader = lazyWithRetry(() => import('./user-dashboard/components/UserHeader'), 'userheader');
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'), 'landingpage');
const BlogIndex = lazyWithRetry(() => import('./pages/BlogIndex'), 'blogindex');
const BlogPost = lazyWithRetry(() => import('./pages/BlogPost'), 'blogpost');
const PricingPage = lazyWithRetry(() => import('./pages/PricingPage'), 'pricingpage');
const RoadmapPage = lazyWithRetry(() => import('./pages/RoadmapPage'), 'roadmap-page');
const RoiEvaluatorPage = lazyWithRetry(() => import('./pages/RoiEvaluatorPage'), 'roi-evaluator');
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'), 'termsofservice');
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'), 'privacypolicy');
const CustomerSupportPage = lazyWithRetry(() => import('./pages/CustomerSupportPage'), 'customer-support-page');
const Login = lazyWithRetry(() => import('./pages/Login'), 'login');
const Signup = lazyWithRetry(() => import('./pages/Signup'), 'signup');
const UserSignup = lazyWithRetry(() => import('./pages/UserSignup'), 'user-signup');
const CreatePassword = lazyWithRetry(() => import('./pages/CreatePassword'), 'create-password');
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'), 'forgot-password');
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'), 'reset-password');
const DownloadInstall = lazyWithRetry(() => import('./pages/DownloadInstall'), 'download-install');
const PublicSalesProposal = lazyWithRetry(() => import('./pages/PublicSalesProposal'), 'public-sales-proposal');
const AiAssistant = lazyWithRetry(() => import('./pages/AiAssistant.js'), 'ai-assistant');
const ApprovalInbox = lazyWithRetry(() => import('./pages/ApprovalInbox'), 'approval-inbox');
const Overview = lazyWithRetry(() => import('./pages/Overview'), 'overview');
const MyTeam = lazyWithRetry(() => import('./pages/MyTeam'), 'my-team');
const Screenshots = lazyWithRetry(() => import('./pages/Screenshots'), 'screenshots');
const TimelapseVideos = lazyWithRetry(() => import('./pages/TimelapseVideos'), 'timelapse-videos');
const Timesheet = lazyWithRetry(() => import('./pages/Timesheet'), 'timesheet');
const RiskUsers = lazyWithRetry(() => import('./pages/RiskUsers'), 'risk-users');
const Timeline = lazyWithRetry(() => import('./pages/Timeline'), 'timeline');
const Reports = lazyWithRetry(() => import('./pages/Reports'), 'reports');
const RealTimeAlert = lazyWithRetry(() => import('./pages/RealTimeAlert'), 'real-time-alert');
const ApplyLeave = lazyWithRetry(() => import('./pages/ApplyLeave'), 'apply-leave');
const LeaveSummary = lazyWithRetry(() => import('./pages/LeaveSummary'), 'leave-summary');
const ManageLeave = lazyWithRetry(() => import('./pages/ManageLeave'), 'manage-leave');
const ManageHoliday = lazyWithRetry(() => import('./pages/ManageHoliday'), 'manage-holiday');
const ReviewApps = lazyWithRetry(() => import('./pages/ReviewApps'), 'review-apps');
const AppsHistory = lazyWithRetry(() => import('./pages/AppsHistory'), 'apps-history');
const AppsSummary = lazyWithRetry(() => import('./pages/AppsSummary'), 'apps-summary');
const Settings = lazyWithRetry(() => import('./pages/Settings'), 'settings');
const Billing = lazyWithRetry(() => import('./pages/Billing'), 'billing');
const Downloads = lazyWithRetry(() => import('./pages/Downloads'), 'downloads');
const AccountSettings = lazyWithRetry(() => import('./pages/AccountSettings'), 'account-settings');
const ViewImage = lazyWithRetry(() => import('./pages/ViewImage'), 'view-image');
const AdminOnboarding = lazyWithRetry(() => import('./pages/AdminOnboarding'), 'admin-onboarding');
const AdminOnboardingTour = lazyWithRetry(() => import('./components/AdminOnboardingTour'), 'admin-onboarding-tour');
const UserDetail = lazyWithRetry(() => import('./pages/UserDetail'), 'user-detail');
const ManualTime = lazyWithRetry(() => import('./pages/ManualTime'), 'manual-time');
const Payroll = lazyWithRetry(() => import('./pages/Payroll'), 'payroll');
const ApplicationLogs = lazyWithRetry(() => import('./pages/ProductionLogs'), 'application-logs');
const PlatformHealth = lazyWithRetry(() => import('./pages/PlatformHealth'), 'platform-health');
const Founder360 = lazyWithRetry(() => import('./pages/Founder360'), 'founder-360');
const ProductSecurityMonitoring = lazyWithRetry(() => import('./pages/ProductSecurityMonitoring'), 'product-security');
const AdminCouponsPage = lazyWithRetry(() => import('./pages/AdminCouponsPage'), 'admin-coupons');
const AdminDebugTokensPage = lazyWithRetry(() => import('./pages/AdminDebugTokensPage'), 'admin-debug-tokens');
const DocumentationWiki = lazyWithRetry(() => import('./pages/DocumentationWiki.js'), 'documentation-wiki');
const Projects = lazyWithRetry(() => import('./pages/Projects.js'), 'projects');
const WorkforceMap = lazyWithRetry(() => import('./pages/WorkforceMap'), 'workforce-map');

const UserMyWork = lazyWithRetry(() => import('./user-dashboard/pages/UserMyWork'), 'user-my-work');
const UserScreenshots = lazyWithRetry(() => import('./user-dashboard/pages/UserScreenshots'), 'user-screenshots');
const UserTimelapseVideos = lazyWithRetry(() => import('./user-dashboard/pages/UserTimelapseVideos'), 'user-timelapse-videos');
const UserTimesheet = lazyWithRetry(() => import('./user-dashboard/pages/UserTimesheet'), 'user-timesheet');
const UserTimesheetHelp = lazyWithRetry(() => import('./user-dashboard/pages/UserTimesheetHelp'), 'user-timesheet-help');
const UserManualTime = lazyWithRetry(() => import('./user-dashboard/pages/UserManualTime'), 'user-manual-time');
const UserApplyLeave = lazyWithRetry(() => import('./user-dashboard/pages/UserApplyLeave'), 'user-apply-leave');
const UserViewHoliday = lazyWithRetry(() => import('./user-dashboard/pages/UserViewHoliday'), 'user-view-holiday');
const UserAccountSettings = lazyWithRetry(() => import('./user-dashboard/pages/UserAccountSettings'), 'user-account-settings');
const UserFAQ = lazyWithRetry(() => import('./user-dashboard/pages/UserFAQ'), 'user-faq');
const UserPayroll = lazyWithRetry(() => import('./user-dashboard/pages/UserPayroll'), 'user-payroll');
const AdminFAQ = lazyWithRetry(() => import('./pages/AdminFAQ'), 'admin-faq');
const LandingPageOne = lazyWithRetry(() => import('./pages/landing-variants/LandingPageOne'), 'landing-page-1');
const LandingPageTwo = lazyWithRetry(() => import('./pages/landing-variants/LandingPageTwo'), 'landing-page-2');

const ROUTE_PRELOADERS = new Map([
  ['/', LandingPage],
  ['/landing-page-1', LandingPageOne],
  ['/landing-page-2', LandingPageTwo],
  ['/blog', BlogIndex],
  ['/pricing', PricingPage],
  ['/roadmap', RoadmapPage],
  ['/support', CustomerSupportPage],
  ['/customer-support', CustomerSupportPage],
  ['/roi-evaluator', RoiEvaluatorPage],
  ['/login', Login], ['/signup', Signup], ['/user-signup', UserSignup],
  ['/create-password', CreatePassword], ['/download-install', DownloadInstall],
  ['/forgot-password', ForgotPassword], ['/ai-assistant', AiAssistant], ['/overview', Overview], ['/approvals', ApprovalInbox], ['/my-teams', MyTeam],
  ['/screenshots', Screenshots], ['/timelapse-videos', TimelapseVideos], ['/timesheet', Timesheet],
  ['/risk-user-history', RiskUsers], ['/timeline', Timeline], ['/manual-time', ManualTime],
  ['/reports', Reports], ['/real-time-alert', RealTimeAlert], ['/apply-leave', ApplyLeave],
  ['/leave-summary', LeaveSummary], ['/manage-leave', ManageLeave], ['/manage-holiday', ManageHoliday],
  ['/review-usage', ReviewApps], ['/apps-summary', AppsSummary], ['/settings', Settings],
  ['/billing', Billing], ['/downloads', Downloads], ['/account-settings', AccountSettings],
  ['/application-logs', ApplicationLogs],
  ['/product-dashboard', PlatformHealth],
  ['/admin/coupons', AdminCouponsPage],
  ['/admin/debug-tokens', AdminDebugTokensPage],
  ['/founder-360', Founder360],
  ['/product-security', ProductSecurityMonitoring],
  ['/terms-of-service', TermsOfService], ['/privacy-policy', PrivacyPolicy], ['/view-image', ViewImage],
  ['/onboarding', AdminOnboarding], ['/user/my-work', UserMyWork], ['/user/screenshots', UserScreenshots],
  ['/user/timelapse-videos', UserTimelapseVideos], ['/user/timesheet', UserTimesheet],
  ['/user/timesheet-help', UserTimesheetHelp], ['/user/manual-time', UserManualTime],
  ['/user/apply-leave', UserApplyLeave], ['/user/view-holiday', UserViewHoliday],
  ['/user/account-settings', UserAccountSettings], ['/user/faq', UserFAQ], ['/user/payroll', UserPayroll],
  ['/faq', AdminFAQ], ['/projects', Projects], ['/wiki', DocumentationWiki],
]);

const findRoutePreloader = (pathname) => {
  if (pathname.startsWith('/wiki')) return DocumentationWiki;
  if (pathname.startsWith('/reports/')) return Reports;
  if (pathname.startsWith('/payroll')) return Payroll;
  if (pathname.startsWith('/settings/')) return Settings;
  if (pathname.startsWith('/member/')) return UserDetail;
  if (pathname.startsWith('/apps-history')) return AppsHistory;
  if (pathname.startsWith('/reset-password')) return ResetPassword;
  if (pathname.startsWith('/onboarding')) return AdminOnboarding;
  return ROUTE_PRELOADERS.get(pathname);
};

function ScrollToTop() {
  const location = useLocation();
  useLayoutEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  return null;
}

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

function RoutePreloadSignals() {
  useEffect(() => {
    const preloadLinkedRoute = (event) => {
      const anchor = event.target?.closest?.('a[href]');
      if (!anchor) return;
      try {
        const url = new URL(anchor.href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        findRoutePreloader(url.pathname)?.preload?.()?.catch(() => {});
      } catch {
        // Ignore malformed or browser-managed links.
      }
    };

    document.addEventListener('pointerdown', preloadLinkedRoute, { passive: true });
    document.addEventListener('focusin', preloadLinkedRoute);
    document.addEventListener('mouseover', preloadLinkedRoute, { passive: true });
    document.addEventListener('pointerover', preloadLinkedRoute, { passive: true });

    return () => {
      document.removeEventListener('pointerdown', preloadLinkedRoute);
      document.removeEventListener('focusin', preloadLinkedRoute);
      document.removeEventListener('mouseover', preloadLinkedRoute);
      document.removeEventListener('pointerover', preloadLinkedRoute);

    };
  }, []);
  return null;
}

// Exchange the desktop app's short-lived, one-time handoff code before route guards run.

function DesktopAuthGate({ children }) {
  const location = useLocation();
  const exchanging = new URLSearchParams(location.search).has('handoff');
  const [attempt, setAttempt] = useState(0);
  const [handoffState, setHandoffState] = useState(() => ({
    status: exchanging ? 'loading' : 'idle',
    error: '',
  }));

  useEffect(() => {
    const code = new URLSearchParams(location.search).get('handoff');
    if (!code) {
      setHandoffState({ status: 'idle', error: '' });
      return undefined;
    }
    const controller = new AbortController();
    setHandoffState({ status: 'loading', error: '' });
    const apiBase = getApiBaseUrl().replace(/\/$/, '');
    fetch(`${apiBase}/auth/desktop-handoff/exchange/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
      signal: controller.signal,
    }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Desktop sign-in failed');
      resetAppSession('desktop_auth');
      localStorage.setItem('token', data.tokens.access);
      localStorage.setItem('refresh_token', data.tokens.refresh);
      const u = data.user;
      const isAdmin = u.is_admin === true;
      localStorage.setItem('user', JSON.stringify({
        id: u.id, name: u.full_name || u.email, email: u.email,
        organization: u.team_name || 'N/A', role: isAdmin ? 'admin' : 'user',
        is_superuser: u.is_superuser === true,
        profilePicture: u.avatar || null,
      }));
      setSessionCookie(isAdmin ? 'admin' : 'user');
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('handoff');
      if (!searchParams.has('category') && (location.pathname.includes('bug') || location.pathname.includes('feedback'))) {
        searchParams.set('category', 'bug');
      }
      if (location.pathname.includes('bug') || location.pathname.includes('feedback')) {
        searchParams.set('feedback', 'true');
      }
      const searchStr = searchParams.toString() ? `?${searchParams.toString()}` : '';
      const targetPath = location.pathname === '/' ? (isAdmin ? '/overview' : '/user/my-work') : location.pathname;
      const target = `${targetPath}${searchStr}`;
      window.location.replace(window.location.origin + target);
    }).catch((error) => {
      if (error?.name === 'AbortError') return;
      setHandoffState({
        status: 'error',
        error: error?.message || 'The secure desktop sign-in link could not be verified.',
      });
    });
    return () => controller.abort();
  }, [attempt, location.pathname, location.search]);

  if (!exchanging) return children;

  return (
    <main className="system-state-page">
      <div className="system-state-card">
        <img src={appIcon} alt="" width="42" height="42" className="system-state-logo" />
        {handoffState.status === 'error' ? (
          <>
            <UnifiedState
              type="error"
              title="Desktop sign-in could not finish"
              message={handoffState.error}
              actionLabel="Try secure sign-in again"
              onAction={() => setAttempt((value) => value + 1)}
            />
            <button
              type="button"
              className="system-state-secondary"
              onClick={() => window.location.replace('/login')}
            >
              Return to sign in
            </button>
          </>
        ) : (
          <UnifiedState
            type="loading"
            title="Finishing secure desktop sign-in…"
            rows={3}
          />
        )}
      </div>
    </main>
  );
}

// Admin layout wrapper
function AdminLayout() {
  const [paywallModalOpen, setPaywallModalOpen] = useState(false);
  const [activeTrialStatus, setActiveTrialStatus] = useState(null);

  const handleOpenPaywall = useCallback((status) => {
    if (status) setActiveTrialStatus(status);
    setPaywallModalOpen(true);
  }, []);

  const refreshTrialState = useCallback(() => {
    billingService.getTrialStatus().then((status) => {
      setActiveTrialStatus(status);
      if (status?.paywall_state === 'expired') {
        setPaywallModalOpen(true);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    refreshTrialState();
    window.addEventListener('subscriptionUpdated', refreshTrialState);
    window.addEventListener('trialUpdated', refreshTrialState);
    return () => {
      window.removeEventListener('subscriptionUpdated', refreshTrialState);
      window.removeEventListener('trialUpdated', refreshTrialState);
    };
  }, [refreshTrialState]);

  return (
    <>
      <WebSocketLifecycle />
      <RouteTracker />
      <div className="app">
        <Sidebar />
        <div className="main-content">
          <Header />
          <TrialCountdownBanner onOpenPaywall={handleOpenPaywall} />
          <InAppMessageDelivery />
          <WorkContextBar />
          <div className="main-content-body workspace-content-body">
          <WorkspacePageEffects contentSelector=".main-content-body" />
          <Suspense fallback={<RouteLoadingState title="Opening admin workspace…" />}>
          <Routes>
            <Route path="/" element={<Navigate to="/overview" replace />} />
            <Route path="/ai-assistant" element={<AiAssistant />} />
            <Route path="/overview" element={<Overview />} />
            <Route path="/approvals" element={<ApprovalInbox />} />
            <Route path="/my-teams" element={<MyTeam />} />
            <Route path="/workforce-map" element={<WorkforceMap />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/member/:identifier" element={<UserDetail />} />
            <Route path="/screenshots" element={<Screenshots />} />
            <Route path="/timelapse-videos" element={<TimelapseVideos />} />
            <Route path="/timesheet" element={<Timesheet />} />
            <Route path="/risk-user-history" element={<RiskUsers />} />
            <Route path="/timeline" element={<Timeline />} />
            <Route path="/manual-time" element={<ManualTime />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/daily-attendance" element={<Reports />} />
            <Route path="/payroll" element={<AdminRoute><Payroll /></AdminRoute>} />
            <Route path="/payroll/summary" element={<AdminRoute><Payroll /></AdminRoute>} />
            <Route path="/payroll/profile" element={<AdminRoute><Payroll /></AdminRoute>} />
            <Route path="/payroll/employees" element={<AdminRoute><Payroll /></AdminRoute>} />
            <Route path="/payroll/run" element={<AdminRoute><Payroll /></AdminRoute>} />
            <Route path="/payroll/payments" element={<AdminRoute><Payroll /></AdminRoute>} />
            <Route path="/payroll/slips" element={<AdminRoute><Payroll /></AdminRoute>} />
            <Route path="/payroll/settings" element={<AdminRoute><Payroll /></AdminRoute>} />
            <Route path="/reports/monthly-attendance" element={<Reports />} />
            <Route path="/reports/monthly-in-out" element={<Reports />} />
            <Route path="/reports/break-report" element={<Reports />} />
            <Route path="/reports/late-clock-in" element={<Reports />} />
            <Route path="/reports/over-time-report" element={<Reports />} />
            <Route path="/reports/custom-report" element={<Reports />} />
            <Route path="/reports/manual-time-report" element={<Reports />} />
            <Route path="/reports/apps-usage" element={<Reports />} />
            <Route path="/reports/work-activity-log" element={<Reports />} />
            <Route path="/reports/work-load-analysis" element={<Reports />} />
            <Route path="/reports/shift-time" element={<Reports />} />
            <Route path="/reports/summary-report" element={<Reports />} />
            <Route path="/reports/input-activity-report" element={<Reports />} />
            <Route path="/reports/login-ip-report" element={<Reports />} />
            <Route path="/reports/alert-report" element={<Reports />} />
            <Route path="/reports/timesheet-report" element={<Reports />} />
            <Route path="/reports/productivity-report" element={<Reports />} />
            <Route path="/reports/mom-comparison" element={<Reports />} />
            <Route path="/reports/usb-detection" element={<Reports />} />
            <Route path="/reports/download-events" element={<Reports />} />
            <Route path="/reports/clipboard-monitoring" element={<Reports />} />
            <Route path="/reports/device-inventory" element={<Reports />} />
            <Route path="/reports/task-master" element={<Reports />} />
            <Route path="/reports/teams-insight" element={<Reports />} />
            <Route path="/real-time-alert" element={<RealTimeAlert />} />
            <Route path="/apply-leave" element={<ApplyLeave />} />
            <Route path="/leave-summary" element={<LeaveSummary />} />
            <Route path="/manage-leave" element={<ManageLeave />} />
            <Route path="/manage-holiday" element={<ManageHoliday />} />
            <Route path="/review-usage" element={<ReviewApps />} />
            <Route path="/apps-history/:id?" element={<AppsHistory />} />
            <Route path="/apps-summary" element={<AppsSummary />} />
            <Route path="/settings" element={<AdminRoute><Navigate to="/settings/manage-teams" replace /></AdminRoute>} />
            <Route path="/settings/:section" element={<AdminRoute><Settings /></AdminRoute>} />
            <Route path="/billing" element={<AdminRoute><Billing /></AdminRoute>} />
            <Route path="/downloads" element={<Downloads />} />
            <Route path="/account-settings" element={<AccountSettings />} />
            <Route path="/application-logs" element={<AdminRoute><ApplicationLogs /></AdminRoute>} />
            <Route path="/product-dashboard" element={<AdminRoute><PlatformHealth /></AdminRoute>} />
            <Route path="/admin/coupons" element={<AdminRoute><AdminCouponsPage /></AdminRoute>} />
            <Route path="/admin/debug-tokens" element={<AdminRoute><AdminDebugTokensPage /></AdminRoute>} />
            <Route path="/founder-360" element={<AdminRoute><Founder360 /></AdminRoute>} />
            <Route path="/product-security" element={<AdminRoute><ProductSecurityMonitoring /></AdminRoute>} />
            <Route path="/platform-health" element={<Navigate to="/product-dashboard" replace />} />
            <Route path="/production-logs" element={<Navigate to="/application-logs" replace />} />
            <Route path="/faq" element={<AdminFAQ />} />
            <Route path="/feedback" element={<Navigate to="/overview?feedback=true&category=bug" replace />} />
            <Route path="/bug-report" element={<Navigate to="/overview?feedback=true&category=bug" replace />} />
            <Route path="/selfxss" element={<SelfXssRedirect />} />
            <Route path="*" element={<RouteFallback />} />
          </Routes>
          </Suspense>
          </div>
        </div>
      </div>
      <FeedbackWidget />
      <Suspense fallback={null}>
        <RoutedAdminOnboardingTour />
      </Suspense>
      <PaywallModal
        isOpen={paywallModalOpen}
        onClose={() => setPaywallModalOpen(false)}
        trialStatus={activeTrialStatus}
      />
    </>
  );
}

/**
 * Applies the calm workspace motion system to admin pages without requiring
 * every route to duplicate pointer, scroll, and reveal observers.
 */
function WorkspacePageEffects({ contentSelector, excludePageClassName = 'overview' }) {
  const location = useLocation();

  useLayoutEffect(() => {
    const contentArea = document.querySelector(contentSelector);
    const page = Array.from(contentArea?.children || []).find((child) => (
      child instanceof HTMLElement
    ));
    if (!contentArea || !page) return undefined;

    const finishRouteTransition = (event) => {
      if (event.target !== page || event.animationName !== 'workspace-route-enter') return;
      page.classList.remove('workspace-route-enter');
    };

    page.classList.remove('workspace-route-enter');
    void page.offsetWidth;
    page.classList.add('workspace-route-enter');
    page.addEventListener('animationend', finishRouteTransition);

    if (excludePageClassName && page.classList.contains(excludePageClassName)) {
      return () => {
        page.removeEventListener('animationend', finishRouteTransition);
        page.classList.remove('workspace-route-enter');
      };
    }

    page.classList.add('workspace-effects-page');

    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    let pointerX = 0;
    let pointerY = 0;
    let activeScrollTop = page.scrollTop;
    let motionFrame = null;

    const isMotionDisabled = () => (
      reducedMotionQuery.matches || !finePointerQuery.matches
    );

    const renderBackground = () => {
      if (motionFrame !== null) window.cancelAnimationFrame(motionFrame);
      motionFrame = window.requestAnimationFrame(() => {
        motionFrame = null;
        const motionDisabled = isMotionDisabled();
        const x = motionDisabled ? 0 : pointerX * 14;
        const y = motionDisabled ? 0 : (activeScrollTop * -0.1) + (pointerY * 10);
        page.style.setProperty('--workspace-grid-x', `${x}px`);
        page.style.setProperty('--workspace-grid-y', `${y}px`);
      });
    };

    const handlePointerMove = (event) => {
      if (isMotionDisabled()) return;
      const bounds = contentArea.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      pointerX = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
      pointerY = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
      renderBackground();
    };

    const resetPointer = () => {
      pointerX = 0;
      pointerY = 0;
      renderBackground();
    };

    const isPrimaryPageScroller = (element) => (
      element === contentArea
      || element === page
      || (
        page.contains(element)
        && element.clientHeight >= contentArea.clientHeight * 0.72
        && element.scrollHeight > element.clientHeight + 1
      )
    );

    const handleScroll = (event) => {
      const scroller = event.target;
      if (!(scroller instanceof HTMLElement) || isMotionDisabled()) return;
      if (isPrimaryPageScroller(scroller)) {
        activeScrollTop = scroller.scrollTop;
        renderBackground();
      }
    };

    contentArea.addEventListener('pointermove', handlePointerMove, { passive: true });
    contentArea.addEventListener('pointerleave', resetPointer);
    contentArea.addEventListener('scroll', handleScroll, true);
    reducedMotionQuery.addEventListener('change', resetPointer);
    finePointerQuery.addEventListener('change', resetPointer);
    renderBackground();

    let revealObserver = null;
    let mutationObserver = null;
    let scanFrame = null;
    let observationFrame = null;
    let observationPaintFrame = null;
    const registeredTargets = new Set();
    const pendingObservationTargets = new Set();

    const elementTokens = (element) => (
      (element.getAttribute('class') || '').split(/\s+/).filter(Boolean)
    );

    const hasExcludedRevealAncestor = (element) => {
      let ancestor = element;
      while (ancestor && ancestor !== page) {
        const excluded = elementTokens(ancestor).some((token) => (
          /(^|-)(sidebar|modal|overlay|tooltip|dropdown|popover|drawer|dialog|header|nav|tabs?|controls?|filters?|search|actions?|footer|icon|avatar|badge|menu|form)(-|$)/i.test(token)
        ));
        if (
          excluded
          || ancestor.getAttribute('role') === 'dialog'
          || ['HEADER', 'NAV', 'FOOTER', 'FORM'].includes(ancestor.tagName)
        ) return true;
        ancestor = ancestor.parentElement;
      }
      return false;
    };

    const isUsableRevealTarget = (element) => {
      if (!(element instanceof HTMLElement) || element.offsetHeight < 64 || element.offsetWidth < 180) {
        return false;
      }
      return !hasExcludedRevealAncestor(element);
    };

    const revealPriority = (element) => {
      const tokens = elementTokens(element);
      if (element.tagName === 'SECTION' || tokens.some((token) => /(section|panel)$/i.test(token))) return 3;
      if (tokens.some((token) => /(grid|table-(wrap|wrapper|container)|chart-container)$/i.test(token))) return 2;
      if (tokens.some((token) => /(content|container)$/i.test(token))) return 1;
      return 0;
    };

    const hasAncestorWithPriority = (element, minimumPriority) => {
      let ancestor = element.parentElement;
      while (ancestor && ancestor !== page) {
        if (revealPriority(ancestor) >= minimumPriority && isUsableRevealTarget(ancestor)) return true;
        ancestor = ancestor.parentElement;
      }
      return false;
    };

    const findRevealTargets = () => {
      const semanticTargets = Array.from(page.querySelectorAll('section, [class]'))
        .filter((element) => isUsableRevealTarget(element) && revealPriority(element) > 0);

      const selectedSemanticTargets = semanticTargets.filter((target) => {
        const priority = revealPriority(target);
        if (hasAncestorWithPriority(target, priority)) return false;

        const containsStrongerSurface = semanticTargets.some((candidate) => (
          candidate !== target
          && target.contains(candidate)
          && revealPriority(candidate) > priority
        ));
        if (containsStrongerSurface) return false;
        return true;
      });

      const directTargets = Array.from(page.children).filter((target) => (
        isUsableRevealTarget(target)
        && !selectedSemanticTargets.some((candidate) => target.contains(candidate))
      ));

      return Array.from(new Set([...directTargets, ...selectedSemanticTargets]));
    };

    if (!reducedMotionQuery.matches && 'IntersectionObserver' in window) {
      page.classList.add('is-workspace-reveal-ready');

      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-workspace-visible');
          revealObserver.unobserve(entry.target);
        });
      }, {
        root: contentArea,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08,
      });

      const registerRevealTargets = () => {
        findRevealTargets().forEach((target) => {
          if (registeredTargets.has(target)) return;
          const delayStep = registeredTargets.size % 4;
          registeredTargets.add(target);
          target.classList.add('workspace-reveal-target');
          target.style.setProperty('--workspace-reveal-delay', `${delayStep * 35}ms`);
          pendingObservationTargets.add(target);
        });

        if (pendingObservationTargets.size === 0 || observationFrame !== null) return;
        observationFrame = window.requestAnimationFrame(() => {
          observationFrame = null;
          observationPaintFrame = window.requestAnimationFrame(() => {
            observationPaintFrame = null;
            pendingObservationTargets.forEach((target) => revealObserver.observe(target));
            pendingObservationTargets.clear();
          });
        });
      };

      const scheduleTargetScan = () => {
        if (scanFrame !== null) return;
        scanFrame = window.requestAnimationFrame(() => {
          scanFrame = null;
          registerRevealTargets();
        });
      };

      registerRevealTargets();
      mutationObserver = new MutationObserver(scheduleTargetScan);
      mutationObserver.observe(page, { childList: true, subtree: true });
    }

    return () => {
      if (motionFrame !== null) window.cancelAnimationFrame(motionFrame);
      if (scanFrame !== null) window.cancelAnimationFrame(scanFrame);
      if (observationFrame !== null) window.cancelAnimationFrame(observationFrame);
      if (observationPaintFrame !== null) window.cancelAnimationFrame(observationPaintFrame);
      revealObserver?.disconnect();
      mutationObserver?.disconnect();
      contentArea.removeEventListener('pointermove', handlePointerMove);
      contentArea.removeEventListener('pointerleave', resetPointer);
      contentArea.removeEventListener('scroll', handleScroll, true);
      reducedMotionQuery.removeEventListener('change', resetPointer);
      finePointerQuery.removeEventListener('change', resetPointer);
      page.removeEventListener('animationend', finishRouteTransition);
      page.classList.remove(
        'workspace-effects-page',
        'is-workspace-reveal-ready',
        'workspace-route-enter'
      );
      page.style.removeProperty('--workspace-grid-x');
      page.style.removeProperty('--workspace-grid-y');
      registeredTargets.forEach((target) => {
        target.classList.remove('workspace-reveal-target', 'is-workspace-visible');
        target.style.removeProperty('--workspace-reveal-delay');
      });
    };
  }, [contentSelector, excludePageClassName, location.pathname]);

  return null;
}

// User dashboard layout wrapper
function UserDashboardLayout() {
  return (
    <UserTrackSettingsProvider>
    <>
    <WebSocketLifecycle />
    <RouteTracker />
    <div className="app">
      <UserSidebar />
      <div className="main-content">
        <UserHeader />
        <UserPermissionNotice />
        <InAppMessageDelivery />
        <div className="user-main-content-body workspace-content-body">
          <WorkspacePageEffects contentSelector=".user-main-content-body" />
          <Suspense fallback={<RouteLoadingState title="Opening your workspace…" />}>
          <Routes>
            <Route path="my-work" element={<UserMyWork />} />
            <Route path="projects" element={<Projects />} />
            <Route path="payroll" element={<UserPayroll />} />
            <Route path="screenshots" element={<UserScreenshots />} />
            <Route path="timelapse-videos" element={<UserTimelapseVideos />} />
            <Route path="timesheet" element={<UserTimesheet />} />
            <Route path="timesheet-help" element={<UserTimesheetHelp />} />
            <Route path="manual-time" element={<UserManualTime />} />
            <Route path="apply-leave" element={<UserApplyLeave />} />
            <Route path="view-holiday" element={<UserViewHoliday />} />
            <Route path="faq" element={<UserFAQ />} />
            <Route path="account-settings" element={<UserAccountSettings />} />
            <Route path="wiki" element={<Navigate to="/wiki" replace />} />
            <Route path="wiki/:articleSlug" element={<Navigate to="/wiki/:articleSlug" replace />} />
            <Route path="feedback" element={<Navigate to="/user/my-work?feedback=true&category=bug" replace />} />
            <Route path="bug-report" element={<Navigate to="/user/my-work?feedback=true&category=bug" replace />} />
            <Route path="/user/feedback" element={<Navigate to="/user/my-work?feedback=true&category=bug" replace />} />
            <Route path="/user/bug-report" element={<Navigate to="/user/my-work?feedback=true&category=bug" replace />} />
            <Route path="/users/my-work" element={<Navigate to="/user/my-work" replace />} />
            <Route path="/users/feedback" element={<Navigate to="/user/my-work?feedback=true&category=bug" replace />} />
            <Route path="/users/bug-report" element={<Navigate to="/user/my-work?feedback=true&category=bug" replace />} />
            <Route path="/selfxss" element={<SelfXssRedirect />} />
            <Route
              path="*"
              element={(
                <RouteFallback
                  homePath="/user/my-work"
                  actionLabel="Return to My Work"
                />
              )}
            />
          </Routes>
          </Suspense>
        </div>
      </div>
    </div>
    <FeedbackWidget />
    </>
    </UserTrackSettingsProvider>
  );
}

function OnboardingWorkspace() {
  return (
    <div className="onboarding-workspace-body workspace-content-body">
      <WorkspacePageEffects contentSelector=".onboarding-workspace-body" excludePageClassName="" />
      <AdminOnboarding />
    </div>
  );
}

function RoutedAdminOnboardingTour() {
  const navigate = useNavigate();
  return <AdminOnboardingTour navigate={navigate} />;
}



// Start WebSocket when in protected area; stop on logout (Header) or unmount.
// Refresh near-expired access first so WS handshake doesn't burn reconnects on a stale JWT.
function WebSocketLifecycle() {
  useEffect(() => {
    let cancelled = false;

    const startSecureSocket = async () => {
      await ensureFreshAccessToken();
      if (cancelled) return;
      websocketService.start();
      scheduleAccessTokenRefresh();
    };

    startSecureSocket();

    const onTokenRefreshed = () => {
      websocketService.restartIfDisconnected();
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      ensureFreshAccessToken().then(() => {
        if (cancelled) return;
        scheduleAccessTokenRefresh();
        websocketService.restartIfDisconnected();
      });
    };

    window.addEventListener(ACCESS_TOKEN_REFRESHED_EVENT, onTokenRefreshed);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      window.removeEventListener(ACCESS_TOKEN_REFRESHED_EVENT, onTokenRefreshed);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      websocketService.stop();
    };
  }, []);
  return null;
}

// Protected Route Component – verifies active session before rendering protected views.
// If session is active (or renewable), ensures fresh access token before mounting children.
// If session is inactive or expired, redirects to /login.
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [authState, setAuthState] = useState(() => {
    if (isDemoModeActive()) return 'ready';
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    const refreshToken = typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null;
    if (!token && !refreshToken) return 'unauthenticated';
    if (token && !isAccessTokenNearExpiry(token)) return 'ready';
    return 'verifying';
  });

  useEffect(() => {
    if (authState !== 'verifying') return;
    let cancelled = false;

    verifyOrRefreshSession()
      .then((status) => {
        if (cancelled) return;
        if (status === true || status === 'transient') {
          // Both verified and transient offline/network hiccups preserve the session!
          setAuthState('ready');
        } else {
          if (!localStorage.getItem('refresh_token')) {
            clearAuth();
          }
          setAuthState('unauthenticated');
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (!localStorage.getItem('refresh_token')) {
          clearAuth();
          setAuthState('unauthenticated');
        } else {
          setAuthState('ready');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authState]);

  if (authState === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (authState === 'verifying') {
    return <RouteLoadingState title="Loading your workspace…" />;
  }

  return children;
};

const getUserRole = () => {
  const token = localStorage.getItem('token');
  const tokenRole = getRoleFromToken(token);
  if (tokenRole) {
    try {
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        if (u && ((u.role === 'admin' && tokenRole !== 'admin') || (u.is_admin && tokenRole !== 'admin'))) {
          console.warn('[Security] Client storage role tamper detected. Reverting to token role.');
          u.role = tokenRole === 'admin' ? 'admin' : (tokenRole === 'manager' ? 'manager' : 'user');
          u.is_admin = tokenRole === 'admin';
          u.is_manager = tokenRole === 'manager';
          localStorage.setItem('user', JSON.stringify(u));
        }
      }
    } catch (_) {}
    return tokenRole === 'admin' ? 'admin' : (tokenRole === 'manager' ? 'manager' : 'user');
  }

  // Fallback for mock test environments without a 3-part JWT
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (u?.role === 'admin' || u?.is_admin === true) return 'admin';
    if (u?.role === 'manager' || u?.is_manager === true) return 'manager';
    return 'user';
  } catch {
    return null;
  }
};

const AdminRoute = ({ children }) => {
  const role = getUserRole();
  if (role !== 'admin') {
    return <Navigate to={role === 'manager' ? '/overview' : '/user/my-work'} replace />;
  }
  return children;
};

const ManagerOrAdminRoute = ({ children }) => {
  const role = getUserRole();
  if (role !== 'admin' && role !== 'manager') {
    return <Navigate to="/user/my-work" replace />;
  }
  return children;
};

const UserRoute = ({ children }) => {
  const role = getUserRole();
  if (role === 'admin' || role === 'manager') {
    return <Navigate to="/overview" replace />;
  }
  return children;
};

const getAllowEmployeeAccessWebsite = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return true;
    const u = JSON.parse(raw);
    return u.allow_employee_access_website !== false;
  } catch {
    return true;
  }
};

function AllowWebsiteGuard({ children }) {
  if (!getAllowEmployeeAccessWebsite()) {
    return (
      <main className="system-state-page">
        <div className="system-state-card">
          <img src={appIcon} alt="" width="42" height="42" className="system-state-logo" />
          <UnifiedState
            type="empty"
            title="Web dashboard access is disabled"
            message="Your organization has chosen desktop-only access for this account. Continue your work in the Unified Workforce desktop app."
            actionLabel="Sign out"
            onAction={() => {
              resetAppSession('access_disabled');
              window.location.href = '/login';
            }}
          />
        </div>
      </main>
    );
  }
  return children;
}

function useSessionKey() {
  const [key, setKey] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user'))?.id || 0; } catch { return 0; }
  });
  const bump = useCallback((e) => {
    const id = e?.detail?.id || Date.now();
    setKey(id);
  }, []);
  useEffect(() => {
    window.addEventListener('userUpdated', bump);
    return () => window.removeEventListener('userUpdated', bump);
  }, [bump]);
  return key;
}

// Cross-tab session sync: reload on login/logout/user switch — not on quiet access-token rotation.
function useCrossTabSessionSync() {
  useEffect(() => {
    // Keep cross-subdomain session cookie synced with localStorage state
    if (localStorage.getItem('token')) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        setSessionCookie(u.role || 'admin');
      } catch (_) {
        setSessionCookie('admin');
      }
    } else if (getSiteMode() === 'app') {
      clearSessionCookie();
    }

    const handleStorage = (e) => {
      if (e.key === 'user') {
        // Identity changed or cleared in another tab.
        if (process.env.NODE_ENV === 'development') {
          console.debug('[session] storage event: user changed, reloading');
        }
        window.location.reload();
        return;
      }
      if (e.key !== 'token') return;

      // Logout in another tab (token cleared) or login while this tab had none.
      if (!e.newValue || !e.oldValue) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[session] storage event: login/logout, reloading');
        }
        window.location.reload();
        return;
      }

      // Access token rotated in another tab — adopt it and reschedule; do not hard-reload.
      scheduleAccessTokenRefresh();
      websocketService.restartIfDisconnected();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
}

function RedirectToApp() {
  const location = useLocation();
  useLayoutEffect(() => {
    window.location.replace(`${getAppOrigin()}${location.pathname}${location.search}${location.hash}`);
  }, [location.pathname, location.search, location.hash]);
  return <RouteLoadingState title="Opening the app…" />;
}

function RedirectToMarketingHome() {
  useLayoutEffect(() => {
    window.location.replace(`${getMarketingOrigin()}/`);
  }, []);
  return <RouteLoadingState title="Opening the site…" />;
}

function SelfXssRedirect() {
  useLayoutEffect(() => {
    window.location.replace('https://en.wikipedia.org/wiki/Self-XSS');
  }, []);
  return <RouteLoadingState title="Redirecting to security advisory…" />;
}

function SmartRootRoute() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const explicitLanding = searchParams.get('landing') === 'true';
  const isAuthenticated = authService.isAuthenticated();
  const targetPath = authService.getAuthenticatedHomePath() || '/overview';
  const siteMode = getSiteMode();

  useLayoutEffect(() => {
    if (!explicitLanding && isAuthenticated && siteMode === 'marketing') {
      const destination = appHref(targetPath);
      window.location.replace(destination);
    }
  }, [explicitLanding, isAuthenticated, siteMode, targetPath]);

  if (!explicitLanding && isAuthenticated) {
    if (siteMode === 'marketing') {
      return <RouteLoadingState title="Redirecting to your dashboard…" />;
    }
    return <Navigate to={targetPath} replace />;
  }

  return <LandingPage />;
}

function MarketingRoutes() {
  return (
    <Routes>
      <Route path="/" element={<SmartRootRoute />} />
      <Route path="/landing-page-1" element={<LandingPageOne />} />
      <Route path="/landing-page-2" element={<LandingPageTwo />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/roadmap" element={<RoadmapPage />} />
      <Route path="/support" element={<CustomerSupportPage />} />
      <Route path="/customer-support" element={<Navigate to="/support" replace />} />
      <Route path="/roi-evaluator" element={<RoiEvaluatorPage />} />
      <Route path="/download-install" element={<DownloadInstall />} />
      <Route path="/clarity" element={<Navigate to="/" replace />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/selfxss" element={<SelfXssRedirect />} />
      <Route path="/wiki" element={<DocumentationWiki />} />
      <Route path="/wiki/:articleSlug" element={<DocumentationWiki />} />
      <Route path="*" element={<RedirectToApp />} />
    </Routes>
  );
}

function ProductRoutes({ sessionKey, includeLanding }) {
  return (
    <Routes>
      {includeLanding ? <Route path="/" element={<SmartRootRoute />} /> : null}
      <Route path="/landing-page-1" element={<LandingPageOne />} />
      <Route path="/landing-page-2" element={<LandingPageTwo />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/roadmap" element={<RoadmapPage />} />
      <Route path="/support" element={<CustomerSupportPage />} />
      <Route path="/customer-support" element={<Navigate to="/support" replace />} />
      <Route path="/roi-evaluator" element={<RoiEvaluatorPage />} />
      <Route path="/clarity" element={includeLanding ? <Navigate to="/" replace /> : <RedirectToMarketingHome />} />
      <Route path="/sales/proposal" element={<PublicSalesProposal />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/user-signup" element={<UserSignup />} />
      <Route path="/create-password" element={<CreatePassword />} />
      <Route path="/download-install" element={<DownloadInstall />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Navigate to="/onboarding/step-1" replace />} />
      <Route path="/onboarding/:step" element={<ProtectedRoute><AdminRoute><OnboardingWorkspace /></AdminRoute></ProtectedRoute>} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/selfxss" element={<SelfXssRedirect />} />
      <Route path="/view-image" element={<ViewImage />} />
      <Route path="/wiki" element={<DocumentationWiki />} />
      <Route path="/wiki/:articleSlug" element={<DocumentationWiki />} />

      <Route
        path="/user/*"
        element={
          <ProtectedRoute>
            <UserRoute>
              <AllowWebsiteGuard>
                <UserDashboardLayout key={sessionKey} />
              </AllowWebsiteGuard>
            </UserRoute>
          </ProtectedRoute>
        }
      />

      {/* Fallback only — must not use /* or it steals `/` from the landing page. */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <ManagerOrAdminRoute>
              <AdminLayout key={sessionKey} />
            </ManagerOrAdminRoute>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  const sessionKey = useSessionKey();
  useCrossTabSessionSync();
  const siteMode = getSiteMode();

  useEffect(() => {
    setServerMaintenanceActive(false);
    startSessionPresence();
    return () => {
      stopSessionPresence();
    };
  }, []);

  return (
    <DesktopAuthGate>
      <ScrollToTop />
      <RoutePreloadSignals />
      <GlobalVoiceInput />
      <Suspense fallback={<RouteLoadingState />}>
        {siteMode === 'marketing' ? (
          <MarketingRoutes />
        ) : (
          <ProductRoutes sessionKey={sessionKey} includeLanding={siteMode === 'combined'} />
        )}
      </Suspense>
    </DesktopAuthGate>
  );
}

export default App;
